import React from "react";
import { Formik, Form, Field, FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { Input, InputWrapper } from "../composites";
import { Grid, Select, Button } from "@chakra-ui/react";
import _ from "lodash";
import { createTransaction, createUser } from "../../services/crm.service";
import { useShowToastHook } from "../../hooks/show-toast-hook";
import ErrorBoundary from "../ErrorBoundary";

const TYPE = {
  DEPOSIT: "Deposit",
  WITHDRAW: "Withdraw",
  CREDIT_IN: "Credit In",
  CREDIT_OUT: "Credit Out"
};

//group f_name m_name l_name  master_password investor_password  email phone company country zipcode state city address
const userSchema = Yup.object().shape({
  login: Yup.number().nullable(),
  group: Yup.string().required("Group is required!"),
  f_name: Yup.string().min(3).required("First name is required!"),
  l_name: Yup.string().min(3).required("Last name is required!"),
  leverage: Yup.number().min(1).required("Leverage is required!"),
  master_password: Yup.string()
    .required("Master password is required")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
    ),
  investor_password: Yup.string()
    .required("Investor password is required")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
    )
});

const CreateUserForm = ({ groups }: { groups: string }) => {
  const { showError, showSuccess } = useShowToastHook();

  const formik = useFormik({
    initialValues: {
      login: undefined,
      group: "",
      leverage: 100,
      f_name: "",
      m_name: "",
      l_name: "",
      master_password: "",
      investor_password: "",
      email: "",
      phone: "",
      company: "",
      country: "",
      zipcode: "",
      state: "",
      city: "",
      address: ""
    },
    validationSchema: userSchema,
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true);

      try {
        const result = await createUser(values);

        if (result?.data?.status) {
          showSuccess("Account created Successfully " + result?.data?.data);
          actions.resetForm();
        } else {
          showError(
            result?.data?.message ??
              "Something went wrong, please try again later."
          );
        }
      } catch (error) {
        showError("Something went wrong, please try again later.");
      }

      actions.setSubmitting(false);
    }
  });

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Grid
          my={6}
          gap={6}
          width="100%"
          maxWidth={["100%", "100%", "900px", "900px"]}
        >
          <Input
            title="Login"
            type="number"
            name="login"
            value={formik?.values?.login}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            errorMessage={
              formik?.touched?.login && formik?.errors?.login
                ? formik?.errors?.login
                : ""
            }
          />

          <ErrorBoundary>
            <InputWrapper
              title="Group"
              errorMessage={
                formik?.touched?.group && formik?.errors?.group
                  ? formik?.errors?.group
                  : ""
              }
            >
              <Select
                name="group"
                value={formik?.values?.group}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
              >
                <option value={undefined}>Select Group</option>
                {_.map(groups ?? [], (_g) => {
                  if (typeof _g != "string") return null;

                  return <option value={_g}> {_g} </option>;
                })}
              </Select>
            </InputWrapper>
          </ErrorBoundary>

          <Input
            title="Leverage"
            type="number"
            name="leverage"
            value={formik?.values?.leverage}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            errorMessage={
              formik?.touched?.leverage && formik?.errors?.leverage
                ? formik?.errors?.leverage
                : ""
            }
          />

          <Grid
            templateColumns={[
              "repeat(1, 1fr)",
              "repeat(1, 1fr)",
              "repeat(3, 1fr)",
              "repeat(3, 1fr)"
            ]}
            gap={6}
          >
            <Input
              title="First Name"
              type="text"
              name="f_name"
              value={formik?.values?.f_name}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.f_name && formik?.errors?.f_name
                  ? formik?.errors?.f_name
                  : ""
              }
            />

            <Input
              title="Middle Name"
              type="text"
              name="m_name"
              value={formik?.values?.m_name}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.m_name && formik?.errors?.m_name
                  ? formik?.errors?.m_name
                  : ""
              }
            />

            <Input
              title="Last Name"
              type="text"
              name="l_name"
              value={formik?.values?.l_name}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.l_name && formik?.errors?.l_name
                  ? formik?.errors?.l_name
                  : ""
              }
            />
          </Grid>

          <Grid
            templateColumns={[
              "repeat(1, 1fr)",
              "repeat(1, 1fr)",
              "repeat(2, 1fr)",
              "repeat(2, 1fr)"
            ]}
            gap={6}
          >
            <Input
              title="Master Password"
              type="text"
              name="master_password"
              value={formik?.values?.master_password}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.master_password &&
                formik?.errors?.master_password
                  ? formik?.errors?.master_password
                  : ""
              }
            />

            <Input
              title="Investor Password"
              type="text"
              name="investor_password"
              value={formik?.values?.investor_password}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.investor_password &&
                formik?.errors?.investor_password
                  ? formik?.errors?.investor_password
                  : ""
              }
            />
          </Grid>

          <Grid
            templateColumns={[
              "repeat(1, 1fr)",
              "repeat(1, 1fr)",
              "repeat(2, 1fr)",
              "repeat(2, 1fr)"
            ]}
            gap={6}
          >
            <Input
              title="Email"
              type="email"
              name="email"
              value={formik?.values?.email}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.email && formik?.errors?.email
                  ? formik?.errors?.email
                  : ""
              }
            />

            <Input
              title="Phone"
              type="number"
              name="phone"
              value={formik?.values?.phone}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.phone && formik?.errors?.phone
                  ? formik?.errors?.phone
                  : ""
              }
            />

            <Input
              title="Company"
              type="text"
              name="company"
              value={formik?.values?.company}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.company && formik?.errors?.company
                  ? formik?.errors?.company
                  : ""
              }
            />

            <Input
              title="Country"
              type="text"
              name="country"
              value={formik?.values?.country}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.country && formik?.errors?.country
                  ? formik?.errors?.country
                  : ""
              }
            />

            <Input
              title="Zipcode"
              type="text"
              name="zipcode"
              value={formik?.values?.zipcode}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.zipcode && formik?.errors?.zipcode
                  ? formik?.errors?.zipcode
                  : ""
              }
            />

            <Input
              title="State"
              type="text"
              name="state"
              value={formik?.values?.state}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.state && formik?.errors?.state
                  ? formik?.errors?.state
                  : ""
              }
            />

            <Input
              title="City"
              type="text"
              name="city"
              value={formik?.values?.city}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.city && formik?.errors?.city
                  ? formik?.errors?.city
                  : ""
              }
            />

            <Input
              title="Address"
              type="text"
              name="address"
              value={formik?.values?.address}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.address && formik?.errors?.address
                  ? formik?.errors?.address
                  : ""
              }
            />
          </Grid>
          <Button
            colorScheme="blue"
            type="submit"
            isLoading={formik?.isSubmitting}
          >
            Create
          </Button>
        </Grid>
      </form>
    </>
  );
};

export default CreateUserForm;
